<script setup lang="ts">
/**
 * SET-A-PASSWORD BANNER (Aran, 2026-10-16, mockup d/fb71062f state C).
 *
 * Shown on the player after somebody signed in BY CODE and their account has
 * no password. "Set a password" opens the existing Set Password form in
 * Settings; "Not now" or the cross hides it and that is remembered on this
 * device (auth/setPasswordNudge.ts). It disappears by itself once a password
 * is set, because it reads `has_password` live — the flag that form writes.
 */
import { computed, inject } from 'vue'
import { useI18n } from '../../composables/useI18n'
import { shouldShowSetPasswordNudge, dismissSetPasswordNudge, setPasswordNudgeVersion } from '../../auth/setPasswordNudge'

const { t } = useI18n()
const emit = defineEmits<{ setPassword: [] }>()

const auth = inject<{ user?: { value: any } } | null>('auth', null)
const user = computed(() => auth?.user?.value ?? null)

// Re-read on any write to the nudge's storage as well as on a user change:
// the session usually lands before the code sign-in is recorded.
const visible = computed(() => {
  void setPasswordNudgeVersion.value
  return shouldShowSetPasswordNudge(user.value)
})

const dismiss = () => {
  if (user.value?.id) dismissSetPasswordNudge(user.value.id)
}

const go = () => {
  // Going to set one is not a dismissal: if they back out of the form without
  // saving, the banner is still the way back to it.
  emit('setPassword')
}
</script>

<template>
  <div v-if="visible" class="set-password-nudge" role="region" :aria-label="t('auth.nudgeEyebrow')">
    <div class="spn-text">
      <div class="spn-kicker">{{ t('auth.nudgeEyebrow') }}</div>
      <p class="spn-body">{{ t('auth.nudgeBody') }}</p>
    </div>
    <div class="spn-actions">
      <button type="button" class="spn-go" @click="go">{{ t('auth.nudgeSetPassword') }}</button>
      <button type="button" class="spn-later" @click="dismiss">{{ t('auth.nudgeNotNow') }}</button>
    </div>
    <button type="button" class="spn-x" :aria-label="t('auth.nudgeClose')" @click="dismiss">×</button>
  </div>
</template>

<style scoped>
.set-password-nudge {
  position: fixed;
  z-index: 150;
  left: 50%;
  transform: translateX(-50%);
  top: calc(190px + env(safe-area-inset-top, 0px));
  width: calc(100% - 32px);
  max-width: 460px;
  box-sizing: border-box;
  padding: 16px 44px 16px 18px;
  background: #fff;
  border: 1px solid rgba(0, 0, 0, 0.1);
  border-radius: 18px;
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.12), 0 4px 12px rgba(0, 0, 0, 0.06);
  color: #2C2622;
  font-family: inherit;
}

@media (min-width: 768px) {
  .set-password-nudge {
    top: calc(226px + env(safe-area-inset-top, 0px));
    width: 460px;
  }
}

.spn-kicker {
  font-size: 0.75rem;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  font-weight: 700;
  color: var(--ssi-red, #c23a3a);
}

.spn-body {
  margin: 0.3rem 0 0.85rem;
  font-size: 0.9375rem;
  line-height: 1.45;
  color: #2C2622;
}

.spn-actions {
  display: flex;
  gap: 0.5rem;
  align-items: center;
}

.spn-go {
  background: linear-gradient(135deg, var(--ssi-red, #c23a3a) 0%, var(--ssi-red-dark, #9c2c2c) 100%);
  color: #fff;
  border: 0;
  border-radius: 12px;
  padding: 0.65rem 1.1rem;
  font-weight: 600;
  font-size: 0.9375rem;
  font-family: inherit;
  cursor: pointer;
}

.spn-later {
  background: none;
  border: 0;
  color: #6B6560;
  font-weight: 500;
  font-size: 0.9375rem;
  font-family: inherit;
  padding: 0.65rem 0.6rem;
  cursor: pointer;
}

.spn-x {
  position: absolute;
  top: 10px;
  right: 10px;
  width: 30px;
  height: 30px;
  border-radius: 50%;
  background: rgba(0, 0, 0, 0.04);
  border: 1px solid rgba(0, 0, 0, 0.12);
  color: #6B6560;
  font-size: 18px;
  line-height: 1;
  cursor: pointer;
}
</style>
